import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const AXES = [
  { key: 'beginner_friendly', label: 'Beginner Friendly', options: [
    ['very_friendly', 'Very Friendly'], ['moderate', 'Moderate'], ['difficult', 'Difficult'],
  ]},
  { key: 'setup_time', label: 'Setup Time', options: [
    ['short', 'Short (< 5 min)'], ['medium', 'Medium'], ['long', 'Long (> 15 min)'],
  ]},
  { key: 'complexity', label: 'Complexity', options: [
    ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard'], ['expert', 'Expert'],
  ]},
  { key: 'play_length', label: 'Play Length', options: [
    ['under_30', 'Under 30 min'], ['30_60', '30-60 min'], ['1_2h', '1-2 hrs'], ['2h_plus', '2+ hrs'],
  ]},
];

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          data-testid={`${testid}-${n}`}>
          <Star size={20} weight={value >= n ? 'fill' : 'regular'}
            className={value >= n ? 'text-[#F59E0B]' : 'text-[#3f3f50]'} />
        </button>
      ))}
    </div>
  );
}

/**
 * GameRatingWidget — per-game, per-axis rating form.
 * Pulls aggregate + my-rating, lets the user upsert their 6-axis rating.
 */
export default function GameRatingWidget({ slug, gameName }) {
  const [agg, setAgg] = useState(null);
  const [mine, setMine] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    beginner_friendly: null, setup_time: null,
    complexity: null, play_length: null,
    fun_stars: 0, replay_stars: 0,
  });

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const [a, m] = await Promise.all([
        ax().get(`/api/games/${slug}/rating`).catch(() => ({ data: null })),
        ax().get(`/api/games/${slug}/rating/me`).catch(() => ({ data: null })),
      ]);
      setAgg(a.data);
      if (m.data && m.data.rated) {
        setMine(m.data);
        setForm({
          beginner_friendly: m.data.beginner_friendly,
          setup_time: m.data.setup_time,
          complexity: m.data.complexity,
          play_length: m.data.play_length,
          fun_stars: m.data.fun_stars || 0,
          replay_stars: m.data.replay_stars || 0,
        });
      } else {
        setMine(null);
      }
    } catch {}
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await ax().post(`/api/games/${slug}/rating`, {
        beginner_friendly: form.beginner_friendly || null,
        setup_time: form.setup_time || null,
        complexity: form.complexity || null,
        play_length: form.play_length || null,
        fun_stars: form.fun_stars || null,
        replay_stars: form.replay_stars || null,
      });
      toast.success('Rating saved');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rating');
    } finally { setSaving(false); }
  };

  const totalRatings = agg?.total_ratings || 0;

  return (
    <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="game-rating-widget">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors text-left"
        data-testid="rating-widget-toggle"
      >
        <div className="flex items-center gap-2.5">
          <Star size={16} weight="duotone" className="text-[#F59E0B]" />
          <div>
            <p className="text-white text-xs font-semibold">
              {mine ? 'Your rating' : 'Rate this game'}
            </p>
            <p className="text-[#666] text-[10px]">
              {totalRatings > 0
                ? `${totalRatings} player${totalRatings === 1 ? '' : 's'} rated · ${agg?.fun_avg ? `${agg.fun_avg.toFixed(1)}★ fun` : 'no aggregate yet'}`
                : 'Be the first to rate'}
            </p>
          </div>
        </div>
        <CaretDown size={14} weight="bold" className={`text-[#666] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-4 pt-1 space-y-3 border-t border-white/[0.04]">
              {AXES.map(axis => (
                <div key={axis.key}>
                  <p className="text-[10px] uppercase text-[#666] mb-1.5 tracking-wider">{axis.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {axis.options.map(([val, lbl]) => (
                      <button key={val}
                        onClick={() => setForm(f => ({ ...f, [axis.key]: f[axis.key] === val ? null : val }))}
                        className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${form[axis.key] === val
                          ? 'border-[#9D4CDD]/60 bg-[#9D4CDD]/15 text-[#9D4CDD]'
                          : 'border-white/[0.06] text-[#888] hover:border-white/[0.12]'}`}
                        data-testid={`rating-${axis.key}-${val}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <p className="text-[10px] uppercase text-[#666] mb-1.5 tracking-wider">Fun</p>
                  <Stars value={form.fun_stars} onChange={v => setForm(f => ({ ...f, fun_stars: v }))} testid="rating-fun" />
                </div>
                <div>
                  <p className="text-[10px] uppercase text-[#666] mb-1.5 tracking-wider">Replayability</p>
                  <Stars value={form.replay_stars} onChange={v => setForm(f => ({ ...f, replay_stars: v }))} testid="rating-replay" />
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setOpen(false)}
                  className="flex-1 py-2 rounded-lg border border-white/[0.06] text-[#A1A1AA] text-xs hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex-1 py-2 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#7C3AED] disabled:opacity-40"
                  data-testid="rating-save-btn"
                >
                  {saving ? 'Saving…' : mine ? 'Update rating' : 'Save rating'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
